import { useCallback, useMemo } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import {
  LabelType,
  LabelValueAcceptedBy,
  LabelValueSource
} from 'graphql/types';

import { useFeatureFlag } from 'modules/common/hooks';
import { FormTimesheetLabel } from '../labelSchema';
import { getLabelState, isLabelValueEmpty, LabelState } from './utils';
import { useIsSmartSuggestionSettingEnabled } from './useIsSmartSuggestionSettingEnabled';

type useBulkLabelSuggestionsActionsResult = {
  acceptAllLabelSuggestionHandler: () => void;
  denyAllLabelSuggestionHandler: () => void;
  enableTimesheetActions: boolean;
};
type useBulkLabelSuggestionsActionsProps = {
  fieldName: string;
};
export const useBulkLabelSuggestionsActions = ({
  fieldName
}: useBulkLabelSuggestionsActionsProps): useBulkLabelSuggestionsActionsResult => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const { setValue, control } = useFormContext();
  const fields = useWatch({
    name: `${fieldName}`,
    control
  });
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const enableTimesheetActions = useMemo(
    () =>
      (fields ?? []).some((field: FormTimesheetLabel) => {
        if (isEmptyLabelsEnabled) {
          const labelState = getLabelState(
            field.label,
            isSmartSuggestionEnabled
          );
          return labelState === LabelState.SUGGESTED;
        }

        const value = isTimeSignalFederatedSuggestionsEnabled
          ? field?.label?.suggestedValues?.[0]
          : field?.label?.value;
        return (
          field?.label?.valueSource === LabelValueSource.Suggestion &&
          field?.label?.type !== LabelType.Comment &&
          !isLabelValueEmpty(value) &&
          !field?.label?.acceptedBy
        );
      }),
    [
      fields,
      isEmptyLabelsEnabled,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled
    ]
  );
  const setLabelValue = useCallback(
    (name: string, value: FieldValues) => {
      setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [setValue]
  );
  const acceptAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.label?.suggestedValues?.[0]
        : field?.label?.value;
      const labelState = getLabelState(field.label, isSmartSuggestionEnabled);

      if (
        isEmptyLabelsEnabled
          ? labelState === LabelState.SUGGESTED
          : field?.label?.valueSource === LabelValueSource.Suggestion &&
            value &&
            !field?.label?.acceptedBy
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          acceptedBy: field?.label?.acceptedBy ?? LabelValueAcceptedBy.User,
          valueSource: field?.label?.valueSource ?? LabelValueSource.User,
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? {
                value: field?.label?.suggestedValues?.[0]
              }
            : {})
        });
      }
    });
  }, [
    fields,
    isTimeSignalFederatedSuggestionsEnabled,
    isSmartSuggestionEnabled,
    isEmptyLabelsEnabled,
    setLabelValue,
    fieldName
  ]);

  const denyAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.label?.suggestedValues?.[0]
        : field?.label?.value;
      const labelState = getLabelState(field.label, isSmartSuggestionEnabled);
      if (
        isEmptyLabelsEnabled
          ? labelState === LabelState.SUGGESTED
          : field?.label?.valueSource === LabelValueSource.Suggestion &&
            value &&
            !field?.label?.acceptedBy
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          value: null,
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? {
                suggestedValues: [],
                suggestionMetadata: null,
                thresholdMet: false
              }
            : {}),
          valueSource: LabelValueSource.Suggestion,
          acceptedBy: null
        });
      }
    });
  }, [
    fields,
    isTimeSignalFederatedSuggestionsEnabled,
    isSmartSuggestionEnabled,
    isEmptyLabelsEnabled,
    setLabelValue,
    fieldName
  ]);

  return {
    enableTimesheetActions,
    acceptAllLabelSuggestionHandler,
    denyAllLabelSuggestionHandler
  };
};
